'use client'

import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useFormDataStore } from '@/stores/formData'
import { COUNTRY_CODES, type CountryCodeOption } from '@/constants/countryCodes'
import { Send, AlertCircle, CheckCircle } from 'lucide-react'

interface FormField {
  id: string
  label: string
  inputType?:
    | 'text'
    | 'phone'
    | 'tel'
    | 'email'
    | 'textarea'
    | 'number'
    | 'password'
    | 'toggle'
    | 'select'
    | 'checkbox'
  type?:
    | 'text'
    | 'phone'
    | 'tel'
    | 'email'
    | 'textarea'
    | 'number'
    | 'password'
    | 'toggle'
    | 'select'
    | 'checkbox'
  variableName: string
  placeholder?: string
  required?: boolean
  options?: Array<{
    value: string
    label: string
  }>
  defaultValue?: string | boolean
  validation?: {
    minLength?: number
    maxLength?: number
    pattern?: string
    errorMessage?: string
    min?: number
    max?: number
  }
  // Extended version 2 properties (optional)
  trueLabel?: string
  falseLabel?: string
  multi?: boolean // for select multi
  selectRenderMode?: 'dropdown' | 'checkbox_group'
  advancedValidation?: {
    requireNumber?: boolean
    requireSymbol?: boolean
    requireUppercase?: boolean
    requireLowercase?: boolean
    integerOnly?: boolean
    allowNegative?: boolean
    precision?: number
    minSelected?: number
    maxSelected?: number
  }
  phoneConfig?: {
    allowedCountries?: string[]
    defaultCountry?: string
    requireCountry?: boolean
    normalizeE164?: boolean
  }
}

interface FormNodeConfig {
  message: string
  fields: FormField[]
  formName?: string
  formVariableName?: string
  submitButtonText?: string
  cancelButtonText?: string
  cancelActionValue?: string
  allowSkip?: boolean
  skipButtonText?: string
  skipActionValue?: string
  successMessage?: string
  thankYouMessage?: string
}

interface FormNodeProps {
  nodeId: string
  config: FormNodeConfig
  sessionId: string
  onSubmit: (formData: Record<string, any>) => void
  onNext?: (message?: string) => void
  onCancelAction?: (actionValue: string) => void
  onSkipAction?: (actionValue: string) => void
}

const normalizeAllowedCountryValues = (allowed?: string[]): Set<string> => {
  if (!allowed || allowed.length === 0) {
    return new Set()
  }

  return new Set(
    allowed
      .map(value => value.trim().toUpperCase())
      .filter(value => value.length > 0)
  )
}

const getCountryOptions = (allowed?: string[]): CountryCodeOption[] => {
  const normalizedAllowed = normalizeAllowedCountryValues(allowed)

  if (normalizedAllowed.size === 0) {
    return COUNTRY_CODES
  }

  const filtered = COUNTRY_CODES.filter(option => {
    const iso = option.iso2.toUpperCase()
    const code = option.code.toUpperCase()
    const digits = option.code.replace(/\D/g, '')

    return (
      normalizedAllowed.has(iso) ||
      normalizedAllowed.has(code) ||
      normalizedAllowed.has(digits)
    )
  })

  return filtered.length > 0 ? filtered : COUNTRY_CODES
}

const DEFAULT_COUNTRY_OPTION: CountryCodeOption = COUNTRY_CODES.find(
  option => option.code === '+60'
) ??
  COUNTRY_CODES[0] ?? {
    iso2: 'MY',
    code: '+60',
    label: 'MY (+60)',
  }

const findPreferredCountryOption = (
  options: CountryCodeOption[],
  preferred?: string
): CountryCodeOption | undefined => {
  if (!preferred) {
    return undefined
  }

  const trimmed = preferred.trim()
  if (trimmed.length === 0) {
    return undefined
  }

  const upper = trimmed.toUpperCase()
  const digitsOnly = trimmed.replace(/\D/g, '')

  return options.find(option => {
    if (option.iso2.toUpperCase() === upper) {
      return true
    }
    if (option.code.toUpperCase() === upper) {
      return true
    }
    if (
      digitsOnly.length > 0 &&
      option.code.replace(/\D/g, '') === digitsOnly
    ) {
      return true
    }
    return false
  })
}

const getPreferredCountryOption = (
  options: CountryCodeOption[],
  preferred?: string
): CountryCodeOption => {
  return (
    findPreferredCountryOption(options, preferred) ||
    options[0] ||
    DEFAULT_COUNTRY_OPTION
  )
}

export default function FormNode({
  nodeId,
  config,
  sessionId,
  onSubmit,
  onNext,
  onCancelAction,
  onSkipAction,
}: FormNodeProps) {
  const [formData, setFormData] = useState<Record<string, any>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [actionFeedback, setActionFeedback] = useState<
    'skip' | 'cancel' | null
  >(null)
  // Dedicated phone state to prevent country code reset & local variable inconsistencies
  const [phoneState, setPhoneState] = useState<
    Record<string, { code: string; number: string }>
  >({})

  // Debug: Log the config to see what fields are being received
  // useEffect(() => {
  //   console.log('📋 [FORM NODE] Received config:', config)
  //   console.log('📋 [FORM NODE] Fields:', config.fields)
  //   config.fields.forEach((field, index) => {
  //     console.log(`📋 [FORM NODE] Field ${index}:`, {
  //       id: field.id,
  //       label: field.label,
  //       inputType: field.inputType,
  //       type: (field as any).type,
  //       variableName: field.variableName,
  //     })
  //   })
  // }, [config])

  const {
    setFieldValue,
    submitForm,
    isSubmitting: storeSubmitting,
  } = useFormDataStore()

  // Initialize form data
  useEffect(() => {
    const initialData: Record<string, any> = {}
    const initialPhoneState: Record<string, { code: string; number: string }> =
      {}
    config.fields.forEach(field => {
      // Support both 'inputType' and 'type' properties
      const fieldType = field.inputType || (field as any).type || 'text'
      // Use defaultValue for toggle fields, array for multi-checkbox/multi-select
      if (fieldType === 'toggle') {
        initialData[field.id] = field.defaultValue ?? false
      } else if (fieldType === 'checkbox') {
        // Multi-checkbox if has options, otherwise single boolean
        if (field.options && field.options.length > 0) {
          initialData[field.id] = Array.isArray(field.defaultValue)
            ? field.defaultValue
            : [] // Start with empty array for multi-checkbox
        } else {
          initialData[field.id] = field.defaultValue ?? false
        }
      } else if (fieldType === 'select' && field.multi) {
        initialData[field.id] = Array.isArray(field.defaultValue)
          ? field.defaultValue
          : [] // multi-select starts empty array
      } else {
        initialData[field.id] =
          field.defaultValue !== undefined ? field.defaultValue : ''
      }
      if (fieldType === 'phone' || fieldType === 'tel') {
        const countryOptions = getCountryOptions(
          field.phoneConfig?.allowedCountries
        )
        const defaultOption = getPreferredCountryOption(
          countryOptions,
          field.phoneConfig?.defaultCountry
        )
        initialPhoneState[field.id] = {
          code: defaultOption.code,
          number: '',
        }
      }
    })
    setFormData(initialData)

    if (Object.keys(initialPhoneState).length > 0) {
      setPhoneState(prev => {
        let mutated = false
        const merged = { ...prev }

        Object.entries(initialPhoneState).forEach(([fieldId, state]) => {
          if (!merged[fieldId]) {
            merged[fieldId] = state
            mutated = true
          }
        })

        return mutated ? merged : prev
      })
    }
  }, [config.fields])

  const validateField = (field: FormField, value: any): string | null => {
    // Support both 'inputType' and 'type' properties
    const fieldType = field.inputType || (field as any).type || 'text'

    // Toggle field validation
    if (fieldType === 'toggle') {
      if (field.required && !value) {
        return `${field.label} must be enabled`
      }
      return null
    }

    // Checkbox validation - handle both single and multi-checkbox
    if (fieldType === 'checkbox') {
      if (field.options && field.options.length > 0) {
        // Multi-checkbox validation
        const arr = Array.isArray(value) ? value : []
        const count = arr.length
        if (field.required && count === 0) {
          return `${field.label} requires at least one selection`
        }
        if (
          field.advancedValidation?.minSelected !== undefined &&
          count < field.advancedValidation.minSelected
        ) {
          return `Select at least ${field.advancedValidation.minSelected} option(s)`
        }
        if (
          field.advancedValidation?.maxSelected !== undefined &&
          count > field.advancedValidation.maxSelected
        ) {
          return `Select at most ${field.advancedValidation.maxSelected} option(s)`
        }
      } else {
        // Single checkbox validation
        if (field.required && !value) {
          return `${field.label} must be checked`
        }
      }
      return null
    }

    // Multi-select validation
    if (fieldType === 'select' && field.multi) {
      const arr = Array.isArray(value) ? value : []
      const count = arr.length
      if (field.required && count === 0) {
        return `${field.label} requires at least one selection`
      }
      if (
        field.advancedValidation?.minSelected !== undefined &&
        count < field.advancedValidation.minSelected
      ) {
        return `Select at least ${field.advancedValidation.minSelected}`
      }
      if (
        field.advancedValidation?.maxSelected !== undefined &&
        count > field.advancedValidation.maxSelected
      ) {
        return `Select at most ${field.advancedValidation.maxSelected}`
      }
      return null
    }

    if (field.required && (!value || value.toString().trim() === '')) {
      return `${field.label} is required`
    }

    if (field.validation) {
      const strValue = value?.toString() || ''

      if (
        field.validation.minLength &&
        strValue.length < field.validation.minLength
      ) {
        return (
          field.validation.errorMessage ||
          `Minimum ${field.validation.minLength} characters required`
        )
      }

      if (
        field.validation.maxLength &&
        strValue.length > field.validation.maxLength
      ) {
        return (
          field.validation.errorMessage ||
          `Maximum ${field.validation.maxLength} characters allowed`
        )
      }

      if (
        field.validation.pattern &&
        !new RegExp(field.validation.pattern).test(strValue)
      ) {
        return field.validation.errorMessage || 'Invalid format'
      }
    }

    // Email validation
    if (fieldType === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(value)) {
        return 'Please enter a valid email address'
      }
    }

    // Phone validation (relaxed: allow leading 0 and country codes, 8-15 digits)
    if ((fieldType === 'phone' || fieldType === 'tel') && value) {
      const normalized = value.replace(/[\s\-\(\)]/g, '')
      const digitsOnly = normalized.startsWith('+')
        ? normalized.slice(1)
        : normalized
      const isNumeric = /^\d+$/.test(digitsOnly)
      if (!isNumeric || digitsOnly.length < 8 || digitsOnly.length > 15) {
        return 'Please enter a valid phone number'
      }
    }

    // Number validation
    if (fieldType === 'number' && value !== '') {
      const numValue = Number(value)
      if (isNaN(numValue)) {
        return 'Please enter a valid number'
      }
      const minValue = field.validation?.min
      if (typeof minValue === 'number' && Number.isFinite(minValue)) {
        if (numValue < minValue) {
          return `Value must be at least ${minValue}`
        }
      }
      const maxValue = field.validation?.max
      if (typeof maxValue === 'number' && Number.isFinite(maxValue)) {
        if (numValue > maxValue) {
          return `Value must be at most ${maxValue}`
        }
      }
      if (
        field.advancedValidation?.integerOnly &&
        !Number.isInteger(numValue)
      ) {
        return 'Value must be an integer'
      }
      if (field.advancedValidation?.precision !== undefined) {
        const decimals = value.includes('.') ? value.split('.')[1].length : 0
        if (decimals > field.advancedValidation.precision) {
          return `Max ${field.advancedValidation.precision} decimal places allowed`
        }
      }
      if (field.advancedValidation?.allowNegative === false && numValue < 0) {
        return 'Negative values not allowed'
      }
    }

    // Password complexity validation (always enforced with sensible defaults)
    if (fieldType === 'password' && typeof value === 'string' && value) {
      // Apply minimum length (default to 8 if not specified)
      const minLength = field.validation?.minLength ?? 8
      if (value.length < minLength) {
        return `Password must be at least ${minLength} characters`
      }

      // Get flags with defaults (all true for password fields)
      const flags = field.advancedValidation || {}
      const requireNumber = flags.requireNumber ?? true
      const requireSymbol = flags.requireSymbol ?? true
      const requireUppercase = flags.requireUppercase ?? true
      const requireLowercase = flags.requireLowercase ?? true

      if (requireNumber && !/[0-9]/.test(value)) {
        return 'Password must contain at least one number'
      }
      if (requireSymbol && !/[!@#$%^&*(),.?":{}|<>_+=\-]/.test(value)) {
        return 'Password must contain at least one special character'
      }
      if (requireUppercase && !/[A-Z]/.test(value)) {
        return 'Password must contain at least one uppercase letter'
      }
      if (requireLowercase && !/[a-z]/.test(value)) {
        return 'Password must contain at least one lowercase letter'
      }
    }

    return null
  }

  const handleFieldChange = (fieldId: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }))

    const field = config.fields.find(f => f.id === fieldId)
    if (field) {
      const nextValue = value
      const strValue = nextValue?.toString?.() ?? ''

      // Validate live when user has entered something (avoid showing "required" while empty).
      const shouldValidateLive = strValue.trim().length > 0
      if (shouldValidateLive) {
        const nextError = validateField(field, nextValue)
        setErrors(prev => ({
          ...prev,
          [fieldId]: nextError || '',
        }))
      } else if (errors[fieldId]) {
        // Clear any previous error while editing; submit will enforce required/empty checks.
        setErrors(prev => ({ ...prev, [fieldId]: '' }))
      }
    }

    // Update Zustand store
    if (field) {
      const fieldType = field.inputType || field.type || 'text'
      setFieldValue(fieldId, {
        fieldId,
        variableName: field.variableName,
        value,
        label: field.label,
        inputType: fieldType,
      })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (actionFeedback) return

    // Validate all fields
    const newErrors: Record<string, string> = {}
    config.fields.forEach(field => {
      const error = validateField(field, formData[field.id])
      if (error) {
        newErrors[field.id] = error
      }
    })

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setIsSubmitting(true)

    try {
      // Submit to Zustand store
      const submissionId = await submitForm(
        nodeId,
        sessionId,
        `Form Submission - ${nodeId}`,
        'Chat transcript will be added here...' // This will be populated by the chat system
      )

      // Transform form data from field IDs to variable names
      const transformedFields: Record<string, any> = {}
      config.fields.forEach(field => {
        if (formData[field.id] !== undefined) {
          transformedFields[field.variableName] = formData[field.id]
        }
      })

      // Also save to database directly
      try {
        // Import the API functions
        const { api } = await import('@/lib/api')

        const response = await api.post('/chat/form-submit', {
          formNodeId: nodeId,
          sessionId: sessionId,
          formName: config.formName || `Form Submission - ${nodeId}`,
          formVariableName: config.formVariableName,
          fields: transformedFields, // Use transformed data with variable names
          chatTranscript: 'Chat transcript will be added here...',
          userId: null, // Regular chat users don't have user IDs
        })
        const responseData: any =
          (response && (response as any).data) ?? response

        // Check if there's an end node message to display
        try {
          if (responseData?.endNodeExecuted && responseData?.endNodeMessage) {
            // Store the end node message to display after form success
            setTimeout(() => {
              if (onNext) {
                onNext(responseData.endNodeMessage)
              }
            }, 2000) // 2 second delay to show success message first
          } else {
            // No end node message, proceed normally
            setTimeout(() => {
              onNext?.()
            }, 1500)
          }
        } catch (parseError) {
          console.error(
            'Failed to handle form submission response:',
            parseError
          )
          // Fallback to normal progression
          setTimeout(() => {
            onNext?.()
          }, 1500)
        }
      } catch (dbError) {
        console.error('Database submission error:', dbError)
        console.error('Failed to save form to database')
        // Don't fail the form submission if database save fails
        setTimeout(() => {
          onNext?.()
        }, 1500)
      }

      // Call the onSubmit callback with form data
      onSubmit({
        submissionId,
        formData,
        nodeId,
        sessionId,
      })

      setIsSubmitted(true)
    } catch (error) {
      console.error('Form submission error:', error)
      setErrors({ general: 'Failed to submit form. Please try again.' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderField = (field: FormField) => {
    const hasError = !!errors[field.id]
    const fieldValue = formData[field.id] ?? ''

    // Support both 'inputType' and 'type' properties for backward compatibility
    const fieldType = field.inputType || (field as any).type || 'text'

    switch (fieldType) {
      case 'phone':
      case 'tel': {
        const digitsOnly = (val: string) => val.replace(/[^0-9]/g, '')
        const formatPhoneNumber = (digits: string) => {
          if (digits.length <= 3) return digits
          if (digits.length <= 6) {
            return `${digits.slice(0, 3)}-${digits.slice(3)}`
          }
          return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
        }
        const countryOptions = getCountryOptions(
          field.phoneConfig?.allowedCountries
        )
        const defaultOption = getPreferredCountryOption(
          countryOptions,
          field.phoneConfig?.defaultCountry
        )
        const fallbackCode = defaultOption.code
        const isAllowedCode = (code: string) =>
          countryOptions.some(option => option.code === code)

        const syncPhoneState = (
          code: string,
          number: string,
          shouldSyncValue: boolean
        ) => {
          setPhoneState(prev => ({
            ...prev,
            [field.id]: { code, number },
          }))
          if (shouldSyncValue) {
            const nextValue = number.length > 0 ? code + number : ''
            handleFieldChange(field.id, nextValue)
          }
        }

        const existingState = phoneState[field.id]

        if (!existingState) {
          if (typeof fieldValue === 'string' && fieldValue.startsWith('+')) {
            const match = fieldValue.match(/^(\+\d{1,4})(\d*)$/)
            if (match) {
              const candidateCode = isAllowedCode(match[1])
                ? match[1]
                : fallbackCode
              syncPhoneState(candidateCode, match[2], true)
            } else {
              const sanitized = digitsOnly(fieldValue)
              syncPhoneState(fallbackCode, sanitized, sanitized.length > 0)
            }
          } else {
            const sanitized =
              typeof fieldValue === 'string' ? digitsOnly(fieldValue) : ''
            syncPhoneState(fallbackCode, sanitized, sanitized.length > 0)
          }
        } else if (!isAllowedCode(existingState.code)) {
          const sanitized = digitsOnly(existingState.number)
          syncPhoneState(fallbackCode, sanitized, true)
        }

        const current = phoneState[field.id] || {
          code: fallbackCode,
          number: typeof fieldValue === 'string' ? digitsOnly(fieldValue) : '',
        }

        const handleCodeChange = (newCode: string) => {
          const nextCode = isAllowedCode(newCode) ? newCode : fallbackCode
          const number = current.number
          setPhoneState(prev => ({
            ...prev,
            [field.id]: {
              ...(prev[field.id] || current),
              code: nextCode,
            },
          }))
          handleFieldChange(
            field.id,
            number.length > 0 ? nextCode + number : ''
          )
        }

        const handleNumberChange = (val: string) => {
          const clean = digitsOnly(val).slice(0, 15)
          setPhoneState(prev => ({
            ...prev,
            [field.id]: {
              ...(prev[field.id] || current),
              number: clean,
            },
          }))
          handleFieldChange(
            field.id,
            clean.length > 0 ? current.code + clean : ''
          )
        }

        return (
          <div key={field.id} className="space-y-2">
            <Label htmlFor={field.id} className="flex items-center gap-1">
              {field.label}
              {field.required && <span className="text-red-500">*</span>}
            </Label>
            <div className="flex gap-2">
              <Select value={current.code} onValueChange={handleCodeChange}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {countryOptions.map(opt => (
                    <SelectItem key={opt.code} value={opt.code}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                id={field.id}
                type="tel"
                placeholder={field.placeholder || 'Phone number'}
                value={formatPhoneNumber(current.number)}
                onChange={e => handleNumberChange(e.target.value)}
                className={hasError ? 'border-red-500' : ''}
                autoComplete="tel"
              />
            </div>
            {hasError && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors[field.id]}
              </p>
            )}
          </div>
        )
      }
      case 'textarea':
        return (
          <div key={field.id} className="space-y-2">
            <Label htmlFor={field.id} className="flex items-center gap-1">
              {field.label}
              {field.required && <span className="text-red-500">*</span>}
            </Label>
            <Textarea
              id={field.id}
              placeholder={field.placeholder}
              value={fieldValue}
              onChange={e => handleFieldChange(field.id, e.target.value)}
              className={hasError ? 'border-red-500' : ''}
              minRows={4}
            />
            {hasError && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors[field.id]}
              </p>
            )}
          </div>
        )

      case 'number':
        return (
          <div key={field.id} className="space-y-2">
            <Label htmlFor={field.id} className="flex items-center gap-1">
              {field.label}
              {field.required && <span className="text-red-500">*</span>}
            </Label>
            <Input
              id={field.id}
              type="number"
              placeholder={field.placeholder}
              value={fieldValue}
              min={field.validation?.min}
              max={field.validation?.max}
              onChange={e => handleFieldChange(field.id, e.target.value)}
              className={hasError ? 'border-red-500' : ''}
            />
            {hasError && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors[field.id]}
              </p>
            )}
          </div>
        )

      case 'password':
        return (
          <div key={field.id} className="space-y-2">
            <Label htmlFor={field.id} className="flex items-center gap-1">
              {field.label}
              {field.required && <span className="text-red-500">*</span>}
            </Label>
            <Input
              id={field.id}
              type="password"
              placeholder={field.placeholder}
              value={fieldValue}
              onChange={e => handleFieldChange(field.id, e.target.value)}
              className={hasError ? 'border-red-500' : ''}
            />
            {hasError && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors[field.id]}
              </p>
            )}
          </div>
        )

      case 'select':
        if (field.multi) {
          const selected: string[] = Array.isArray(fieldValue) ? fieldValue : []
          const toggleOption = (val: string) => {
            let next: string[]
            if (selected.includes(val)) {
              next = selected.filter(v => v !== val)
            } else {
              next = [...selected, val]
            }
            handleFieldChange(field.id, next)
          }
          return (
            <div key={field.id} className="space-y-2">
              <Label className="flex items-center gap-1">
                {field.label}
                {field.required && <span className="text-red-500">*</span>}
              </Label>
              <div className="space-y-1">
                {(field.options || []).map(option => {
                  const checked = selected.includes(option.value)
                  return (
                    <div
                      key={option.value}
                      className="flex items-center space-x-2"
                    >
                      <Checkbox
                        id={`${field.id}_${option.value}`}
                        checked={checked}
                        onCheckedChange={() => toggleOption(option.value)}
                      />
                      <Label
                        htmlFor={`${field.id}_${option.value}`}
                        className="cursor-pointer"
                      >
                        {option.label}
                      </Label>
                    </div>
                  )
                })}
              </div>
              {hasError && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors[field.id]}
                </p>
              )}
            </div>
          )
        }
        return (
          <div key={field.id} className="space-y-2">
            <Label htmlFor={field.id} className="flex items-center gap-1">
              {field.label}
              {field.required && <span className="text-red-500">*</span>}
            </Label>
            <Select
              value={fieldValue}
              onValueChange={value => handleFieldChange(field.id, value)}
            >
              <SelectTrigger className={hasError ? 'border-red-500' : ''}>
                <SelectValue
                  placeholder={field.placeholder || 'Select an option'}
                />
              </SelectTrigger>
              <SelectContent>
                {(field.options || []).map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasError && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors[field.id]}
              </p>
            )}
          </div>
        )

      case 'toggle':
        return (
          <div key={field.id} className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor={field.id} className="flex items-center gap-1">
                {field.label}
                {field.required && <span className="text-red-500">*</span>}
              </Label>
              <Switch
                id={field.id}
                checked={!!formData[field.id]}
                onCheckedChange={checked =>
                  handleFieldChange(field.id, checked)
                }
              />
            </div>
            {(field.trueLabel || field.falseLabel) && (
              <div className="text-xs text-muted-foreground flex gap-2">
                <span>
                  {formData[field.id]
                    ? field.trueLabel || 'On'
                    : field.falseLabel || 'Off'}
                </span>
              </div>
            )}
            {hasError && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors[field.id]}
              </p>
            )}
          </div>
        )

      case 'checkbox':
        // Check if checkbox has options (multi-checkbox) or is a single checkbox
        if (field.options && field.options.length > 0) {
          // Multi-checkbox group
          const selectedValues = Array.isArray(formData[field.id])
            ? formData[field.id]
            : []

          return (
            <div key={field.id} className="space-y-2">
              <Label className="flex items-center gap-1">
                {field.label}
                {field.required && <span className="text-red-500">*</span>}
              </Label>
              <div className="space-y-2 pl-2">
                {field.options.map(option => (
                  <div
                    key={option.value}
                    className="flex items-center space-x-2"
                  >
                    <Checkbox
                      id={`${field.id}-${option.value}`}
                      checked={selectedValues.includes(option.value)}
                      onCheckedChange={checked => {
                        const newValues = checked
                          ? [...selectedValues, option.value]
                          : selectedValues.filter(v => v !== option.value)
                        handleFieldChange(field.id, newValues)
                      }}
                    />
                    <Label
                      htmlFor={`${field.id}-${option.value}`}
                      className="cursor-pointer font-normal"
                    >
                      {option.label}
                    </Label>
                  </div>
                ))}
              </div>
              {hasError && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors[field.id]}
                </p>
              )}
            </div>
          )
        } else {
          // Single checkbox (backward compatibility)
          return (
            <div key={field.id} className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={field.id}
                  checked={!!formData[field.id]}
                  onCheckedChange={checked =>
                    handleFieldChange(field.id, checked)
                  }
                />
                <Label
                  htmlFor={field.id}
                  className="flex items-center gap-1 cursor-pointer"
                >
                  {field.label}
                  {field.required && <span className="text-red-500">*</span>}
                </Label>
              </div>
              {hasError && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors[field.id]}
                </p>
              )}
            </div>
          )
        }

      default:
        return (
          <div key={field.id} className="space-y-2">
            <Label htmlFor={field.id} className="flex items-center gap-1">
              {field.label}
              {field.required && <span className="text-red-500">*</span>}
            </Label>
            <Input
              id={field.id}
              type={
                fieldType === 'phone' || fieldType === 'tel' ? 'tel' : fieldType
              }
              placeholder={field.placeholder}
              value={fieldValue}
              onChange={e => handleFieldChange(field.id, e.target.value)}
              className={hasError ? 'border-red-500' : ''}
            />
            {hasError && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors[field.id]}
              </p>
            )}
          </div>
        )
    }
  }

  const handleCancel = async () => {
    const actionValue = config.cancelActionValue
    if (!actionValue || !onCancelAction) {
      return
    }

    try {
      setActionFeedback('cancel')
      await Promise.resolve(onCancelAction(actionValue))
    } catch (error) {
      console.error('Form cancel action error:', error)
      setErrors({ general: 'Failed to cancel. Please try again.' })
      setActionFeedback(null)
    }
  }

  const handleSkip = async () => {
    const actionValue = config.skipActionValue
    if (!actionValue || !onSkipAction) {
      return
    }

    try {
      setActionFeedback('skip')
      await Promise.resolve(onSkipAction(actionValue))
    } catch (error) {
      console.error('Form skip action error:', error)
      setErrors({ general: 'Failed to skip. Please try again.' })
      setActionFeedback(null)
    }
  }

  if (isSubmitted) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="p-6 text-center">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-green-700 mb-2">
            {config.successMessage || 'Form Submitted Successfully!'}
          </h3>
          <p className="text-gray-600">
            {config.thankYouMessage ||
              'Thank you for your submission. Processing your request...'}
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="text-lg">{config.message}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <fieldset
            disabled={
              isSubmitting || storeSubmitting || actionFeedback !== null
            }
            className={actionFeedback ? 'opacity-70' : undefined}
          >
            {config.fields.map(renderField)}
          </fieldset>

          {errors.general && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.general}
              </p>
            </div>
          )}

          {actionFeedback && (
            <div
              className={`p-3 rounded-md border text-sm flex items-center gap-2 ${
                actionFeedback === 'skip'
                  ? 'bg-blue-50 border-blue-200 text-blue-700'
                  : 'bg-amber-50 border-amber-200 text-amber-700'
              }`}
            >
              {actionFeedback === 'skip' ? (
                <CheckCircle className="h-4 w-4" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              {actionFeedback === 'skip'
                ? 'Form skipped. Continuing...'
                : 'Form canceled. Returning...'}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {config.allowSkip && config.skipActionValue && onSkipAction && (
              <Button
                type="button"
                variant="outline"
                className="flex-1 min-w-[120px]"
                onClick={handleSkip}
                disabled={
                  isSubmitting || storeSubmitting || actionFeedback !== null
                }
              >
                {config.skipButtonText || 'Skip'}
              </Button>
            )}
            {config.cancelButtonText &&
              config.cancelActionValue &&
              onCancelAction && (
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 min-w-[120px]"
                  onClick={handleCancel}
                  disabled={
                    isSubmitting || storeSubmitting || actionFeedback !== null
                  }
                >
                  {config.cancelButtonText}
                </Button>
              )}
            <Button
              type="submit"
              className="flex-1 min-w-[120px]"
              disabled={
                isSubmitting || storeSubmitting || actionFeedback !== null
              }
            >
              {isSubmitting || storeSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  {config.submitButtonText || 'Submit'}
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
